import { Load } from "components/load";
import PageError from "components/page-error";
import { IoRefresh } from "react-icons/io5";
import {
  useAnalyzeExcelMutation,
  useConfirmBatchMutation,
  useDownloadTemplateMutation,
  useGetFilteredStoresQuery,
  useUpdateStoreResponsibleMutation,
} from "@/services/store/store";
import { useFilterState } from "../ducks/filter/hooks/filterState";
import { useColumns } from "./columns";
import SelectFilters from "./components/select-filters";
import Table from "./components/table";
import { StoresContainer, TableWithFiltersContainer } from "./styles";
import { useForm, FormProvider } from "react-hook-form";
import { useGetAnalystsOptionsQuery } from "@/services/analyst-assignment/select-options";
import { useShuffle } from "./utils/shuffle";
import { useEffect, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { MESSAGES } from "./utils/messages";
import { toast } from "components/ui/use-toast";
import { RoleEnum } from "../../../../enum/role";
import { IAnalystAssignmentFormData } from "../../../../@types/analyst-assignment";
import { handeOrderBy } from "../../../../utils/pagination";
import { formatFilterValue } from "../../../../utils/formatters";
import { ArrowLeft, Download, UploadIcon } from "lucide-react";
import TableLoad from "components/load/TableLoad";
import * as XLSX from "xlsx";
import { Button } from "components/ui/button";
import { useNavigate } from "react-router-dom";
import { RegionalModalContent } from "@/pages/conciliation/analyst-assignment/list/components/modal";
import { useRegionalsOptions } from "../ducks/filter/hooks/use-regionals-options";
import {
  AnalyzeExcelResponse,
  ConfirmBatchRequest,
  ExcelLine,
} from "../types/excel-upload.types";
import { downloadFromBlob } from "@/utils/download-blob";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { AmbiguityResolutionContent } from "./components/ambiguity-resolution-content";
import { StatusEnum } from "@/enum/status";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";

export const AnalystAssignment: React.FC = () => {
  const navigate = useNavigate();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };

  const [originalStoreData, setOriginalStoreData] = useState<
    Map<number, { regional_id: number; responsible_id: string }>
  >(new Map());

  const [isSheetLoading, setIsSheetLoading] = useState(false);
  const [analysisResult, setAnalysisResult] =
    useState<AnalyzeExcelResponse | null>(null);

  const { handleModal } = useDialogModal();

  const [updateStoreResponsible, { isLoading: isUpdateLoading }] =
    useUpdateStoreResponsibleMutation();

  const [downloadTemplate] = useDownloadTemplateMutation();

  const methods = useForm();
  const { handleSubmit: handleFormSubmit, setValue, getValues, reset, control } = methods;
  const [disabled, setDisabled] = useState<boolean>(false);

  const { data: analystsOptions } = useGetAnalystsOptionsQuery({
    role: RoleEnum.ANALISTA,
  });

  const [analyzeExcel, { isLoading: isAnalyzing }] = useAnalyzeExcelMutation();
  const [confirmBatch, { isLoading: isConfirming }] = useConfirmBatchMutation();

  const { options: regionalResponsibleOptions, refetch: refetchRegionals } =
    useRegionalsOptions(StatusEnum.ACTIVE, 999);

  const columns = useColumns(
    control,
    setValue,
    getValues,
    analystsOptions,
    regionalResponsibleOptions,
    setDisabled
  );
  const { handleRaffleAnalysts } = useShuffle();

  const { code, responsible, id, regional, order: orderObj } = filters;
  const orderBy = handeOrderBy(orderObj || { column: "id", direction: "desc" });

  const {
    data: res,
    isLoading,
    refetch,
    isFetching,
    error,
  } = useGetFilteredStoresQuery({
    orderBy: JSON.stringify(orderBy),
    ...(id !== undefined && { id: formatFilterValue(id) }),
    ...(code !== undefined && { code: formatFilterValue(code) }),
    ...(responsible !== undefined && {
      responsible: formatFilterValue(responsible),
    }),
    ...(regional !== undefined && { regional: formatFilterValue(regional) }),
    perPage: 300,
    page: filters?.meta?.page || 1,
  });

  const { data } = res ?? { data: [] };

  useEffect(() => {
    if (data.length > 0) {
      const originalData = new Map();
      const formValues: Record<string, any> = {};

      data.forEach((store: any) => {
        originalData.set(store.code, {
          regional_id: store.regional?.id,
          responsible_id: store.responsible?.id,
        });

        const storeKey = `store_${store.code}`;
        formValues[storeKey] = {};

        if (store.regional?.id) {
          formValues[storeKey].regional = store.regional.id.toString();
        }
        if (store.responsible?.id) {
          formValues[storeKey].analyst = store.responsible.id;
        }
      });

      reset(formValues);
      setOriginalStoreData(originalData);
    }
  }, [data]);

  if (error) {
    return <PageError status={500} message={"ERRO INTERNO NO SERVIDOR"} />;
  }

  if (isLoading) {
    return <Load />;
  }

  const validateForm = async (formData: IAnalystAssignmentFormData) => {
    const payload = Object.keys(formData)
      .map((key) => {
        const store_code = Number(key.split("store_")[1]);
        const formValues = formData[key as any];
        const original = originalStoreData.get(store_code);

        if (!formValues.analyst) {
          return null;
        }

        const regionalChanged =
          original?.regional_id !== Number(formValues.regional);
        const analystChanged = original?.responsible_id !== formValues.analyst;

        if (!regionalChanged && !analystChanged) {
          return null;
        }

        return {
          store_code,
          responsible_id: formValues.analyst,
          regional_id: Number(formValues.regional),
          regional_name: regionalResponsibleOptions.find(
            (reg) => Number(reg.value) === Number(formValues.regional)
          )?.label!,
        };
      })
      .filter(Boolean) as Array<{
      store_code: number;
      responsible_id: string;
      regional_id: number;
      regional_name: string;
    }>;

    try {
      if (payload.length > 0) {
        await updateStoreResponsible(payload);
        toast(MESSAGES.UPDATE.SUCCESS);

        payload.forEach((item) => {
          originalStoreData.set(item.store_code, {
            regional_id: item.regional_id,
            responsible_id: item.responsible_id,
          });
        });
        setOriginalStoreData(new Map(originalStoreData));
      } else {
        toast(MESSAGES.UPDATE.NO_CHANGES);
      }
    } catch (e) {
      console.error(e);
      toast(MESSAGES.UPDATE.ERROR);
    }
  };

  const handleSheetAssign = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      setIsSheetLoading(true);

      const excelData = await readExcelFile(file);

      const analysis = await analyzeExcel({ data: excelData }).unwrap();
      setAnalysisResult(analysis);

      if (analysis.errors.length > 0) {
        toast(MESSAGES.EXCEL.ERRORS_FOUND(analysis.errors.length));
        console.error("Erros:", analysis.errors);
        setIsSheetLoading(false);
        return;
      }

      if (analysis.ambiguous.length > 0) {
        openAmbiguityModal(analysis);
        setIsSheetLoading(false);
        return;
      }

      await handleConfirmBatch(analysis);
    } catch (error: any) {
      toast(MESSAGES.EXCEL.PROCESSING_ERROR(error?.data?.message));
      setIsSheetLoading(false);
    } finally {
      event.target.value = "";
    }
  };

  const openAmbiguityModal = (analysis: AnalyzeExcelResponse) => {
    handleModal({
      isOpen: true,
      title: MESSAGES.EXCEL.AMBIGUITY_TITLE(analysis.ambiguous.length),
      description: MESSAGES.EXCEL.AMBIGUITY_DESCRIPTION(
        analysis.ambiguous.length
      ),
      element: (
        <AmbiguityResolutionContent
          ambiguousItems={analysis.ambiguous}
          onConfirm={(resolutions) =>
            handleAmbiguityResolution(resolutions, analysis)
          }
          onCancel={handleAmbiguityCancel}
        />
      ),
    });
  };

  const readExcelFile = (file: File): Promise<ExcelLine[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          const json: (string | number | undefined)[][] =
            XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const excelData: ExcelLine[] = json
            .filter((row) => {
              if (String(row[0]).toLowerCase() === "loja") {
                return false;
              }
              if (!row[0] || !row[1]) {
                return false;
              }
              if (isNaN(Number(row[0]))) {
                return false;
              }
              return true;
            })
            .map((row) => ({
              store_code: Number(row[0]),
              regional_name: String(row[1]).trim(),
            }));

          resolve(excelData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error("Erro ao ler arquivo"));
      reader.readAsArrayBuffer(file);
    });
  };

  const createStoreMap = (stores: any[]) => {
    return new Map(stores.map((s) => [s.id, s]));
  };

  const handleConfirmBatch = async (analysis?: AnalyzeExcelResponse) => {
    const dataToUse = analysis || analysisResult;

    if (!dataToUse) {
      return;
    }

    try {
      setIsSheetLoading(true);

      const payload: ConfirmBatchRequest = {
        ready: dataToUse.ready.map((item) => ({
          store_id: item.store_id,
          regional_id: item.regional_id,
          regional_name: item.regional_name,
        })),
        resolutions: [],
        create_and_assign: dataToUse.to_create.map((item) => ({
          regional_name: item.name,
          store_ids: item.assign_to_store_ids,
        })),
      };

      if (dataToUse.ready.length === 0 && dataToUse.to_create.length === 0) {
        toast(MESSAGES.EXCEL.NO_CHANGES);
        setIsSheetLoading(false);
        return;
      }

      const result = await confirmBatch(payload).unwrap();

      await refetchRegionals();
      await refetch();

      toast(
        MESSAGES.EXCEL.BATCH_SUCCESS(
          result.updated_stores,
          result.created_regionals.length
        )
      );

      setAnalysisResult(null);
      setIsSheetLoading(false);
    } catch (error: any) {
      toast(MESSAGES.EXCEL.CONFIRM_ERROR(error?.data?.message));
      setIsSheetLoading(false);
    }
  };

  const handleAmbiguityResolution = async (
    resolutions: Array<{ store_id: number; regional_id: number }>,
    analysis: AnalyzeExcelResponse
  ) => {
    try {
      setIsSheetLoading(true);

      const storeMap = createStoreMap(data);

      const changedResolutions = resolutions.filter((item) => {
        const currentStore = storeMap.get(item.store_id);
        return currentStore?.regional?.id !== item.regional_id;
      });

      const regionalNameById = new Map(
        (regionalResponsibleOptions ?? []).map((o) => [
          Number(o.value),
          o.label,
        ])
      );

      const payload: ConfirmBatchRequest = {
        ready: analysis.ready.map((item) => ({
          store_id: item.store_id,
          regional_id: item.regional_id,
          regional_name: item.regional_name,
        })),
        resolutions: changedResolutions.map((item) => ({
          store_id: item.store_id,
          regional_id: item.regional_id,
          regional_name: regionalNameById.get(item.regional_id) ?? "",
        })),
        create_and_assign: analysis.to_create.map((item) => ({
          regional_name: item.name,
          store_ids: item.assign_to_store_ids,
        })),
      };

      const hasInvalidResolution = payload.resolutions.some(
        (r) => !r.regional_name?.trim()
      );
      if (hasInvalidResolution) {
        toast(MESSAGES.EXCEL.DATA_ERROR);
        return;
      }

      if (
        payload.ready.length === 0 &&
        payload.resolutions.length === 0 &&
        payload.create_and_assign.length === 0
      ) {
        toast(MESSAGES.EXCEL.NO_CHANGES);
        handleModal({ isOpen: false });
        setAnalysisResult(null);
        return;
      }

      const result = await confirmBatch(payload).unwrap();

      await refetchRegionals();
      await refetch();

      toast(
        MESSAGES.EXCEL.BATCH_SUCCESS(
          result.updated_stores,
          result.created_regionals.length
        )
      );

      handleModal({ isOpen: false });
      setAnalysisResult(null);
    } catch (error: any) {
      toast(MESSAGES.EXCEL.CONFIRM_ERROR(error?.data?.message));
    } finally {
      setIsSheetLoading(false);
    }
  };

  const handleAmbiguityCancel = () => {
    handleModal({ isOpen: false });
    setAnalysisResult(null);
    setIsSheetLoading(false);
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsSheetLoading(true);

      const blob = await downloadTemplate().unwrap();

      downloadFromBlob(
        blob,
        `regionais_template_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      toast(MESSAGES.TEMPLATE.DOWNLOAD_SUCCESS);
    } catch (error: any) {
      toast(MESSAGES.TEMPLATE.DOWNLOAD_ERROR);
    } finally {
      setIsSheetLoading(false);
    }
  };

  return (
    <>
      <PageLayout>
        <div className="flex justify-start w-full items-center gap-4">
          <ArrowLeft className="cursor-pointer" onClick={() => navigate("/conciliacao/lojas")} />
          <PageTitle title={"Atribuição de Analista Financeiro para Lojas"} />
        </div>
        <ContentContainer>
          <StoresContainer>
            <TableWithFiltersContainer>
              <div className="stats flex justify-between items-center pb-2">
                <span className="text-[#2B0C1A] font-normal leading-[25px]">
                  Contagem: {data.length}
                </span>
                <div className="flex gap-4 items-center">
                  <span
                    className={`refetch ${isFetching ? "is-loading" : ""}`}
                    onClick={() => {
                      refetch();
                    }}
                  >
                    <IoRefresh />
                    Recarregar
                  </span>

                  <button
                    className="refetch"
                    onClick={handleDownloadTemplate}
                    disabled={isSheetLoading}
                  >
                    <Download size={18} />
                    {isSheetLoading ? "Gerando..." : "Baixar Template"}
                  </button>
                </div>
              </div>

              <div className="filters-table flex items-center justify-between w-full">
                <div className="filters flex items-center justify-between w-content gap-3">
                  <SelectFilters />
                </div>
                <div className="flex gap-3 items-center">
                  <button
                    className="raffle-btn rounded-md"
                    onClick={() =>
                      handleModal({
                        isOpen: true,
                        title: "Adicionar Regionais",
                        description:
                          "Digite o nome de uma regional e pressione Enter para adicioná-la à lista.",
                        element: <RegionalModalContent />,
                      })
                    }
                  >
                    Adicionar Regional
                  </button>
                  <label
                    htmlFor="file-upload"
                    className={`h-8 px-8 w-max border rounded-md border-[#DCDCE3] ${
                      isAnalyzing || isConfirming || isSheetLoading
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-[#F5CB2B] cursor-pointer"
                    } text-[#2B0C1A] text-sm flex items-center gap-2 transition-colors duration-200`}
                  >
                    <UploadIcon className="w-4 h-4" />
                    <span className="hover:underline">
                      {isAnalyzing
                        ? "Analisando..."
                        : isConfirming
                        ? "Confirmando..."
                        : "Adicionar arquivo"}
                    </span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      onChange={handleSheetAssign}
                      disabled={isAnalyzing || isConfirming || isSheetLoading}
                    />
                  </label>
                  <Button
                    className="h-8 bg-[#F5CB2B] text-[#2B0C1A] hover:bg-[#F5CB2B]/90"
                    onClick={() => {
                      setDisabled(false);
                      handleRaffleAnalysts(getValues, reset, data, analystsOptions);
                    }}
                  >
                    Sortear lojas
                  </Button>
                </div>
              </div>
              <div className="relative transition-all duration-300">
                <FormProvider {...methods}>
                  <Table data={data} columns={columns} isFetching={isFetching} />
                </FormProvider>
                {isSheetLoading && <TableLoad />}
              </div>
            </TableWithFiltersContainer>
            <div className="flex items-center justify-end gap-4 w-full mt-5">
              <Button
                variant="outline"
                className="h-8"
                onClick={() => navigate("/conciliacao/lojas")}
              >
                Voltar
              </Button>
              <Tooltip open={disabled}>
                <TooltipTrigger asChild>
                  <Button
                    className="h-8 bg-[#f5cb2b] font-bold text-[#2b0c1a] hover:bg-[#f5cb2b]/90"
                    disabled={disabled || isUpdateLoading || isFetching}
                    onClick={() => handleFormSubmit(validateForm)()}
                  >
                    Salvar
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-black text-white text-center bubble">
                  <div className="max-w-[80px]">
                    Selecione todas linhas para salvar!
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          </StoresContainer>
        </ContentContainer>
      </PageLayout>
    </>
  );
};
